import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import type { SemanticGPUResponse } from '../../types/gpu';
import { getApiUrl } from '../../utils/api';

// --- Pure transform functions ---

type ImageStatus = 'custom_image' | 'base_provider_image' | 'no_image' | 'architecture_mismatch';

interface CoverageStats {
  total: number;
  customImage: number;
  baseImage: number;
  noImage: number;
  archMismatch: number;
  coveragePercent: number;
}

function computeStats(instances: SemanticGPUResponse[]): CoverageStats {
  const total = instances.length;
  const customImage = instances.filter(i => i.image_status === 'custom_image').length;
  const baseImage = instances.filter(i => i.image_status === 'base_provider_image').length;
  const noImage = instances.filter(i => i.image_status === 'no_image').length;
  const archMismatch = instances.filter(i => i.image_status === 'architecture_mismatch').length;
  const covered = customImage + baseImage;
  const coveragePercent = total > 0 ? Math.round((covered / total) * 100) : 0;
  return { total, customImage, baseImage, noImage, archMismatch, coveragePercent };
}

interface RegionGroup {
  region: string;
  instances: SemanticGPUResponse[];
  stats: CoverageStats;
}

interface ProviderGroup {
  provider: string;
  regions: RegionGroup[];
  stats: CoverageStats;
}

function groupByProvider(instances: SemanticGPUResponse[]): ProviderGroup[] {
  const byProvider = new Map<string, Map<string, SemanticGPUResponse[]>>();

  for (const inst of instances) {
    if (!byProvider.has(inst.provider)) {
      byProvider.set(inst.provider, new Map());
    }
    const regionMap = byProvider.get(inst.provider)!;
    if (!regionMap.has(inst.region)) {
      regionMap.set(inst.region, []);
    }
    regionMap.get(inst.region)!.push(inst);
  }

  return Array.from(byProvider.entries())
    .map(([provider, regionMap]) => {
      const regions = Array.from(regionMap.entries())
        .map(([region, regionInstances]) => ({
          region,
          instances: regionInstances.sort((a, b) =>
            (a.provider_instance_id ?? '').localeCompare(b.provider_instance_id ?? '')
          ),
          stats: computeStats(regionInstances),
        }))
        .sort((a, b) => a.region.localeCompare(b.region));

      const allInstances = regions.flatMap(r => r.instances);
      return { provider, regions, stats: computeStats(allInstances) };
    })
    .sort((a, b) => a.provider.localeCompare(b.provider));
}

interface GapRow {
  provider: string;
  region: string;
  instanceType: string;
  gpuInfo: string;
  status: 'no_image' | 'architecture_mismatch';
  reason: string;
}

function extractGaps(instances: SemanticGPUResponse[]): GapRow[] {
  return instances
    .filter(i => i.image_status === 'no_image' || i.image_status === 'architecture_mismatch')
    .map(i => ({
      provider: i.provider,
      region: i.region,
      instanceType: i.provider_instance_id,
      gpuInfo: i.gpu_count && i.gpu_model_name
        ? `${i.gpu_count}x ${i.gpu_model_name}${i.gpu_memory_per_device_gb ? ` ${i.gpu_memory_per_device_gb}GB` : ''}`
        : 'N/A',
      status: i.image_status as 'no_image' | 'architecture_mismatch',
      reason: i.image_status === 'architecture_mismatch'
        ? `Architecture ${i.gpu_architecture ?? 'unknown'} not supported by available images`
        : 'No compatible image deployed to this region',
    }))
    .sort((a, b) => a.provider.localeCompare(b.provider) || a.region.localeCompare(b.region));
}

// --- Status rendering helpers ---

const STATUS_DOT: Record<ImageStatus, string> = {
  custom_image: 'bg-green-500',
  base_provider_image: 'bg-blue-500',
  no_image: 'bg-yellow-500',
  architecture_mismatch: 'bg-amber-500',
};

const STATUS_LABEL: Record<ImageStatus, string> = {
  custom_image: 'Custom Image',
  base_provider_image: 'Base Image',
  no_image: 'No Image',
  architecture_mismatch: 'Arch Mismatch',
};

// --- Component ---

interface Props {
  onNavigateToCreateTemplate?: () => void;
}

export default function AMICoverageTracker({ onNavigateToCreateTemplate }: Props) {
  const [instances, setInstances] = useState<SemanticGPUResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedProviders, setExpandedProviders] = useState<Set<string>>(new Set());
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());
  const [gapStatusFilter, setGapStatusFilter] = useState<'all' | 'no_image' | 'architecture_mismatch'>('all');
  const [gapProviderFilter, setGapProviderFilter] = useState<string>('all');

  useEffect(() => {
    fetchInstances();
  }, []);

  const fetchInstances = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem('authToken');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }

      const response = await axios.get(`${getApiUrl()}/api/gpu/discover`, {
        params: { admin_view: true, include_stale: true, limit: 0 },
        headers: { 'Authorization': `Bearer ${token}` },
      });

      setInstances(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch GPU catalog:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch coverage data');
    } finally {
      setLoading(false);
    }
  };

  // Derived data
  const stats = useMemo(() => computeStats(instances), [instances]);
  const providerGroups = useMemo(() => groupByProvider(instances), [instances]);
  const allGaps = useMemo(() => extractGaps(instances), [instances]);

  const filteredGaps = useMemo(() => {
    let gaps = allGaps;
    if (gapStatusFilter !== 'all') {
      gaps = gaps.filter(g => g.status === gapStatusFilter);
    }
    if (gapProviderFilter !== 'all') {
      gaps = gaps.filter(g => g.provider === gapProviderFilter);
    }
    return gaps;
  }, [allGaps, gapStatusFilter, gapProviderFilter]);

  const uniqueProviders = useMemo(
    () => Array.from(new Set(instances.map(i => i.provider))).sort(),
    [instances]
  );

  const toggleProvider = (provider: string) => {
    setExpandedProviders(prev => {
      const next = new Set(prev);
      next.has(provider) ? next.delete(provider) : next.add(provider);
      return next;
    });
  };

  const toggleRegion = (key: string) => {
    setExpandedRegions(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-700 rounded w-1/3" />
          <div className="h-4 bg-gray-700 rounded" />
          <div className="h-4 bg-gray-700 rounded w-5/6" />
          <div className="h-4 bg-gray-700 rounded w-4/6" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-900/20 border border-red-800 rounded-xl p-6">
          <h3 className="text-lg font-medium text-red-400">Failed to load coverage data</h3>
          <p className="text-red-300/80 mt-1">{error}</p>
          <button
            onClick={fetchInstances}
            className="mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Image Coverage Tracker</h1>
          <p className="text-sm text-gray-400 mt-1">
            {stats.total} instance types across {providerGroups.length} providers
          </p>
        </div>
        <button
          onClick={fetchInstances}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Section 1: Coverage Summary Strip */}
      <div className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-green-500" />
              <span className="text-sm text-gray-400">Custom Image</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.customImage}</div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500" />
              <span className="text-sm text-gray-400">Base Image</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.baseImage}</div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-yellow-500" />
              <span className="text-sm text-gray-400">No Image</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.noImage}</div>
          </div>
          <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
            <div className="flex items-center space-x-2 mb-1">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500" />
              <span className="text-sm text-gray-400">Arch Mismatch</span>
            </div>
            <div className="text-2xl font-bold text-white">{stats.archMismatch}</div>
          </div>
        </div>

        {/* Coverage bar */}
        <div className="bg-gray-800 rounded-xl p-4 border border-gray-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-400">Overall Coverage</span>
            <span className="text-lg font-bold text-white">{stats.coveragePercent}%</span>
          </div>
          <div className="w-full h-3 bg-gray-700 rounded-full overflow-hidden flex">
            {stats.total > 0 && (
              <>
                <div
                  className="bg-green-500 h-full"
                  style={{ width: `${(stats.customImage / stats.total) * 100}%` }}
                />
                <div
                  className="bg-blue-500 h-full"
                  style={{ width: `${(stats.baseImage / stats.total) * 100}%` }}
                />
                <div
                  className="bg-yellow-500 h-full"
                  style={{ width: `${(stats.noImage / stats.total) * 100}%` }}
                />
                <div
                  className="bg-amber-500 h-full"
                  style={{ width: `${(stats.archMismatch / stats.total) * 100}%` }}
                />
              </>
            )}
          </div>
          <div className="flex items-center space-x-4 mt-2 text-xs text-gray-500">
            <span className="flex items-center space-x-1"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" /> <span>Custom</span></span>
            <span className="flex items-center space-x-1"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" /> <span>Base</span></span>
            <span className="flex items-center space-x-1"><span className="w-2 h-2 rounded-full bg-yellow-500 inline-block" /> <span>No Image</span></span>
            <span className="flex items-center space-x-1"><span className="w-2 h-2 rounded-full bg-amber-500 inline-block" /> <span>Arch Mismatch</span></span>
          </div>
        </div>
      </div>

      {/* Section 2: Provider / Region Tree */}
      <div className="space-y-3">
        <h2 className="text-lg font-semibold text-white">Provider / Region Breakdown</h2>
        {providerGroups.map(pg => (
          <div key={pg.provider} className="bg-gray-800 rounded-xl border border-gray-700">
            <button
              onClick={() => toggleProvider(pg.provider)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-750 transition-colors"
            >
              <div className="flex items-center space-x-3">
                {expandedProviders.has(pg.provider) ? (
                  <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                )}
                <span className="font-medium text-white uppercase">{pg.provider}</span>
                <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">
                  {pg.regions.length} regions
                </span>
              </div>
              <span className="text-sm text-gray-400">
                {pg.stats.coveragePercent}% covered ({pg.stats.customImage + pg.stats.baseImage}/{pg.stats.total})
              </span>
            </button>

            {expandedProviders.has(pg.provider) && (
              <div className="px-4 pb-4 space-y-2">
                {pg.regions.map(rg => {
                  const regionKey = `${pg.provider}:${rg.region}`;
                  return (
                    <div key={rg.region} className="bg-gray-900 rounded-lg border border-gray-700">
                      <button
                        onClick={() => toggleRegion(regionKey)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-gray-800 transition-colors"
                      >
                        <div className="flex items-center space-x-3">
                          {expandedRegions.has(regionKey) ? (
                            <ChevronDownIcon className="h-4 w-4 text-gray-400" />
                          ) : (
                            <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                          )}
                          <span className="text-white text-sm">{rg.region}</span>
                          <span className="text-xs text-gray-500">{rg.instances.length} types</span>
                        </div>
                        <span className="text-xs text-gray-400">
                          {rg.stats.coveragePercent}% covered
                        </span>
                      </button>

                      {expandedRegions.has(regionKey) && (
                        <div className="px-3 pb-3 space-y-1">
                          {rg.instances.map(inst => {
                            const status = (inst.image_status ?? 'no_image') as ImageStatus;
                            return (
                              <div
                                key={inst.id}
                                className="flex items-center justify-between p-2 bg-gray-800 rounded text-sm"
                              >
                                <div className="flex items-center space-x-3">
                                  <div className={`w-2.5 h-2.5 rounded-full ${STATUS_DOT[status]}`} />
                                  <span className="text-white font-mono text-xs">{inst.provider_instance_id}</span>
                                  {inst.gpu_count && inst.gpu_model_name && (
                                    <span className="text-gray-500 text-xs">
                                      {inst.gpu_count}x {inst.gpu_model_name}
                                      {inst.gpu_memory_per_device_gb ? ` ${inst.gpu_memory_per_device_gb}GB` : ''}
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center space-x-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    status === 'custom_image' ? 'bg-green-900/50 text-green-300' :
                                    status === 'base_provider_image' ? 'bg-blue-900/50 text-blue-300' :
                                    status === 'architecture_mismatch' ? 'bg-amber-900/50 text-amber-300' :
                                    'bg-yellow-900/50 text-yellow-300'
                                  }`}>
                                    {STATUS_LABEL[status]}
                                  </span>
                                  {inst.image_count != null && inst.image_count > 0 && (
                                    <span className="text-xs text-gray-500">{inst.image_count} img</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Section 3: Gap Analysis Table */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">
            Gap Analysis
            <span className="ml-2 text-sm font-normal text-gray-400">({filteredGaps.length} gaps)</span>
          </h2>
          <div className="flex items-center space-x-2">
            {/* Status filter */}
            {(['all', 'no_image', 'architecture_mismatch'] as const).map(v => (
              <button
                key={v}
                onClick={() => setGapStatusFilter(v)}
                className={`px-3 py-1 rounded-full text-xs transition-colors ${
                  gapStatusFilter === v
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {v === 'all' ? 'All' : v === 'no_image' ? 'No Image' : 'Arch Mismatch'}
              </button>
            ))}

            {/* Provider filter */}
            <select
              value={gapProviderFilter}
              onChange={e => setGapProviderFilter(e.target.value)}
              className="bg-gray-700 text-gray-300 text-xs rounded-lg px-2 py-1 border border-gray-600"
            >
              <option value="all">All Providers</option>
              {uniqueProviders.map(p => (
                <option key={p} value={p}>{p.toUpperCase()}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredGaps.length === 0 ? (
          <div className="bg-gray-800 rounded-xl border border-gray-700 p-8 text-center">
            <p className="text-gray-400">No coverage gaps found with current filters.</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-700 text-left">
                  <th className="px-4 py-3 text-gray-400 font-medium">Provider</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Region</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Instance Type</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">GPU</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Status</th>
                  <th className="px-4 py-3 text-gray-400 font-medium">Reason</th>
                  {onNavigateToCreateTemplate && (
                    <th className="px-4 py-3 text-gray-400 font-medium">Action</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredGaps.map((gap, i) => (
                  <tr key={`${gap.provider}-${gap.region}-${gap.instanceType}`} className={i % 2 === 0 ? 'bg-gray-800' : 'bg-gray-850'}>
                    <td className="px-4 py-2 text-white uppercase">{gap.provider}</td>
                    <td className="px-4 py-2 text-gray-300">{gap.region}</td>
                    <td className="px-4 py-2 text-white font-mono text-xs">{gap.instanceType}</td>
                    <td className="px-4 py-2 text-gray-300 text-xs">{gap.gpuInfo}</td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${
                        gap.status === 'no_image' ? 'bg-yellow-900/50 text-yellow-300' : 'bg-amber-900/50 text-amber-300'
                      }`}>
                        {gap.status === 'no_image' ? 'No Image' : 'Arch Mismatch'}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-gray-400 text-xs max-w-xs truncate">{gap.reason}</td>
                    {onNavigateToCreateTemplate && (
                      <td className="px-4 py-2">
                        {gap.status === 'no_image' && (
                          <button
                            onClick={onNavigateToCreateTemplate}
                            className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded transition-colors"
                          >
                            Build Image
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
